import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { 
  FiFacebook, 
  FiInstagram, 
  FiTwitter, 
  FiYoutube,
  FiMail,
  FiPhone,
  FiMapPin,
  FiHeart 
} from 'react-icons/fi';

const FooterContainer = styled.footer`
  background: var(--dark-bg);
  color: white;
  padding: 4rem 10% 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled(motion.div)`
  h3 {
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
    position: relative;
    display: inline-block;

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 30px;
      height: 2px;
      background: linear-gradient(135deg, #d395ff, #ff7fd9);
    }
  }
`;

const LogoSection = styled.div`
  .logo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;

    img {
      height: 40px;
      width: auto;
    }

    span {
      font-size: 1.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #d395ff, #ff7fd9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    color: white;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, #d395ff, #ff7fd9);
      transform: translateY(-3px);
    }

    svg {
      font-size: 1.1rem;
    }
  }
`;

const FooterLinks = styled.ul`
  list-style: none;

  li {
    margin-bottom: 0.8rem;
  }

  a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 0.95rem;
    position: relative;
    padding-left: 0;

    &:hover {
      color: white;
      padding-left: 5px;
    }

    &::before {
      content: '›';
      position: absolute;
      left: -15px;
      opacity: 0;
      transition: all 0.3s ease;
      color: var(--primary-color);
    }

    &:hover::before {
      opacity: 1;
      left: -5px;
    }
  }
`;

const ContactInfo = styled.div`
  p {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1rem;
    font-size: 0.95rem;

    svg {
      color: var(--primary-color);
      font-size: 1.2rem;
    }
  }
`;

const NewsletterForm = styled.form`
  margin-top: 1.5rem;

  .input-group {
    display: flex;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;

    &:focus-within {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(211,149,255,0.3);
    }

    input {
      flex: 1;
      padding: 0.8rem 1rem;
      background: transparent;
      border: none;
      color: white;
      font-size: 0.95rem;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }

      &:focus {
        outline: none;
      }
    }

    button {
      padding: 0.8rem 1.2rem;
      background: linear-gradient(135deg, #d395ff, #ff7fd9);
      border: none;
      color: white;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        opacity: 0.9;
      }
    }
  }
`;

const FooterBottom = styled.div`
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .love {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;

    svg {
      color: #ff7fd9;
      animation: heartbeat 1.5s ease infinite;
    }
  }

  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;

const FooterBadge = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  span {
    padding: 0.3rem 1rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 30px;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <FooterContainer>
      <FooterGrid
        as={motion.div}
        variants={footerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <FooterSection variants={itemVariants}>
          <LogoSection>
            <div className="logo">
              <img src="/assets/kemlo-logo.png" alt="Kemlo" />
              <span>Kemlo</span>
            </div>
            <p>
              Crafting sweet moments since 2020. Every piece of Kemlo candy is made 
              with love, creativity, and the finest ingredients to bring joy to your day.
            </p>
            <SocialLinks>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                <FiFacebook />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <FiInstagram />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                <FiTwitter />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer">
                <FiYoutube />
              </a>
            </SocialLinks>
          </LogoSection>
        </FooterSection>

        <FooterSection variants={itemVariants}>
          <h3>Quick Links</h3>
          <FooterLinks>
            <li><Link to="/about">About Us</Link></li>
            <li><Link to="/catalog">Our Products</Link></li>
            <li><Link to="/contact">Contact Us</Link></li>
            <li><Link to="/faq">FAQ</Link></li>
            <li><Link to="/shipping">Shipping Info</Link></li>
          </FooterLinks>
        </FooterSection>

        <FooterSection variants={itemVariants}>
          <h3>Categories</h3>
          <FooterLinks>
            <li><Link to="/catalog?category=fruity">Fruity Toffees</Link></li>
            <li><Link to="/catalog?category=chocolate">Chocolate Collection</Link></li>
            <li><Link to="/catalog?category=mint">Mint Fresh</Link></li>
            <li><Link to="/catalog?category=classic">Classic Favorites</Link></li>
            <li><Link to="/catalog?category=sour">Sour Treats</Link></li>
          </FooterLinks>
        </FooterSection>

        <FooterSection variants={itemVariants}>
          <h3>Get in Touch</h3>
          <ContactInfo>
            <p>
              <FiMapPin />
              B-5 GIRWAI, GWALIOR, MADHYA PRADESH 474001
            </p>
            <p>
              <FiPhone />
              +91 9009206707
            </p>
            <p>
              <FiMail />
              kishanfoodproduct2@gmail.com
            </p>
          </ContactInfo>

          <NewsletterForm onSubmit={(e) => e.preventDefault()}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1rem' }}>Newsletter</h3>
            <div className="input-group">
              <input 
                type="email" 
                placeholder="Your email" 
                required 
              />
              <button type="submit">Subscribe</button>
            </div>
          </NewsletterForm>
        </FooterSection>
      </FooterGrid>

      <FooterBottom
        as={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
      >
        <div>
          © {currentYear} Kemlo. All rights reserved. Designed by PixxyKraft.com
        </div>
        <div className="love">
          Made with <FiHeart /> in Candyland
        </div>
        <FooterBadge>
          <span>Privacy Policy</span>
          <span>Terms of Service</span>
          <span>Cookie Policy</span>
        </FooterBadge>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;